import { z } from 'zod';
import { ERROR_CODE_PATTERN } from './error-codes';

/**
 * Structured log entry shared by server (JSONL files) and browser (IndexedDB buffer → JSONL export).
 * Never put child names, project content, emails, credentials, tokens, IPs, media or whole
 * request bodies in a log entry — `sanitizeContext` enforces the mechanical part of that rule.
 */

export const LOG_SEVERITIES = ['INFO', 'WARN', 'ERROR', 'CRITICAL'] as const;
export const logSeveritySchema = z.enum(LOG_SEVERITIES);
export type LogSeverity = z.infer<typeof logSeveritySchema>;

export const logEntrySchema = z
  .object({
    timestamp: z.iso.datetime(),
    severity: logSeveritySchema,
    module: z.string().min(1).max(64),
    code: z.string().regex(ERROR_CODE_PATTERN).optional(),
    message: z.string().min(1).max(500),
    correlationId: z.string().min(1).max(100),
    origin: z.string().max(200).optional(),
    context: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type LogEntry = z.infer<typeof logEntrySchema>;

/** Keys whose values are always replaced, matched case-insensitively as substrings. */
const SENSITIVE_KEY_FRAGMENTS = [
  'password',
  'passwd',
  'secret',
  'token',
  'cookie',
  'authorization',
  'session',
  'apikey',
  'api_key',
  'credential',
  'email',
  'name',
  'body',
  'project',
  'payload',
];
/** Allowed despite matching a fragment above (non-personal technical fields). */
const ALLOWED_KEYS = new Set([
  'errorname',
  'modulename',
  'filename',
  'routename',
  'component',
  'componentname',
  'migrationname',
  'description',
  'zip',
  'hostname',
  'projectid',
  'projectcount',
]);

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
const IPV4_PATTERN = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;
const BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi;
const URL_CREDENTIALS_PATTERN = /(\w+:\/\/)[^:/@\s]+:[^@/\s]+@/g;

export const REDACTED = '[REDACTED]';
const MAX_DEPTH = 4;
const MAX_STRING = 300;
const MAX_KEYS = 30;
const MAX_ARRAY = 20;

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (ALLOWED_KEYS.has(lower)) return false;
  // `ip` must be a whole token to avoid redacting e.g. "description" or "zip".
  if (/(^|[_-])ip($|[_-])|^ip(address|addr)?$|clientip|remoteaddress/i.test(key)) return true;
  return SENSITIVE_KEY_FRAGMENTS.some((f) => lower.includes(f));
}

export function sanitizeString(value: string): string {
  const cleaned = value
    .replace(URL_CREDENTIALS_PATTERN, `$1${REDACTED}@`)
    .replace(BEARER_PATTERN, `Bearer ${REDACTED}`)
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(IPV4_PATTERN, REDACTED);
  return cleaned.length > MAX_STRING ? `${cleaned.slice(0, MAX_STRING)}…[truncated]` : cleaned;
}

function sanitizeValue(value: unknown, depth: number): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return value.toISOString();
  if (value instanceof Error)
    return { errorName: value.name, message: sanitizeString(value.message) };
  if (depth >= MAX_DEPTH) return '[depth-limit]';
  if (Array.isArray(value)) {
    const items = value.slice(0, MAX_ARRAY).map((v) => sanitizeValue(v, depth + 1));
    if (value.length > MAX_ARRAY) items.push(`[+${value.length - MAX_ARRAY} more]`);
    return items;
  }
  if (typeof value === 'object') {
    return sanitizeRecord(value as Record<string, unknown>, depth + 1);
  }
  return `[${typeof value}]`;
}

function sanitizeRecord(input: Record<string, unknown>, depth: number): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const keys = Object.keys(input);
  for (const key of keys.slice(0, MAX_KEYS)) {
    out[key] = isSensitiveKey(key) ? REDACTED : sanitizeValue(input[key], depth);
  }
  if (keys.length > MAX_KEYS) out['_truncatedKeys'] = keys.length - MAX_KEYS;
  return out;
}

/** Redacts secrets/personal fields and bounds size. Pure and deterministic. */
export function sanitizeContext(context: Record<string, unknown>): Record<string, unknown> {
  return sanitizeRecord(context, 0);
}

export interface CreateLogEntryInput {
  severity: LogSeverity;
  module: string;
  message: string;
  correlationId: string;
  code?: string | undefined;
  origin?: string | undefined;
  context?: Record<string, unknown> | undefined;
  now?: Date | undefined;
}

/** Builds a validated, sanitised entry. Throws only on programmer error (invalid module/code). */
export function createLogEntry(input: CreateLogEntryInput): LogEntry {
  const entry: LogEntry = {
    timestamp: (input.now ?? new Date()).toISOString(),
    severity: input.severity,
    module: input.module,
    message: sanitizeString(input.message).slice(0, 500),
    correlationId: input.correlationId,
  };
  if (input.code !== undefined) entry.code = input.code;
  if (input.origin !== undefined) entry.origin = input.origin;
  if (input.context !== undefined) entry.context = sanitizeContext(input.context);
  return logEntrySchema.parse(entry);
}

export function toJsonLine(entry: LogEntry): string {
  return `${JSON.stringify(entry)}\n`;
}
